import React from 'react';
import { StampConfig, DEFAULT_STAMP_CONFIG } from './DeductionFormConfig';

interface StampComponentProps {
    config?: StampConfig;
    /** Size in pixels (width and height for circle/square; width for rectangle) */
    size?: number;
    /** Rotation in degrees (e.g. -12 for slight tilt) */
    rotate?: number;
    style?: React.CSSProperties;
}

/**
 * Renders an institutional stamp as an inline SVG.
 * Supports three shapes: circle (default), rectangle, and square.
 * Defaults to Hazina SACCOS styling if no config is provided.
 */
const StampComponent: React.FC<StampComponentProps> = ({
    config,
    size = 140,
    rotate,
    style,
}) => {
    const uid = React.useId();
    const c = { ...DEFAULT_STAMP_CONFIG, ...config } as Required<StampConfig>;
    const shape = c.shape;
    const color = c.color;

    const containerStyle: React.CSSProperties = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...(rotate !== undefined ? { transform: `rotate(${rotate}deg)` } : {}),
        ...style,
    };

    if (c.stampImageUrl) {
        return (
            <div style={{ ...containerStyle, width: size, height: size }}>
                <img
                    src={c.stampImageUrl}
                    alt="Institution Stamp"
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
            </div>
        );
    }

    if (shape === 'circle') {
        const cx = 200, cy = 200;
        const outerR = 188, innerR = 150;
        const arcR = 169;
        // Top arc path (left → right along top of circle)
        const topArcD = `M ${cx - arcR},${cy} A ${arcR},${arcR} 0 0,1 ${cx + arcR},${cy}`;
        // Bottom arc path (left → right along bottom of circle, reading direction flipped)
        const bottomArcD = `M ${cx - arcR},${cy} A ${arcR},${arcR} 0 0,0 ${cx + arcR},${cy}`;
        const topId = `topArc-${uid}`;
        const botId = `botArc-${uid}`;
        return (
            <div style={{ ...containerStyle, width: size, height: size }}>
                <svg viewBox="0 0 400 400" width={size} height={size}>
                    <defs>
                        <path id={topId} d={topArcD} />
                        <path id={botId} d={bottomArcD} />
                    </defs>
                    {/* Outer ring */}
                    <circle cx={cx} cy={cy} r={outerR} fill="none" stroke={color} strokeWidth="8" />
                    {/* Inner ring */}
                    <circle cx={cx} cy={cy} r={innerR} fill="none" stroke={color} strokeWidth="3" />
                    {/* Top curved text */}
                    <text fill={color} fontSize="24" fontFamily="Arial" fontWeight="bold">
                        <textPath href={`#${topId}`} startOffset="50%" textAnchor="middle">
                            {c.topText}
                        </textPath>
                    </text>
                    {/* Center lines */}
                    <text x={cx} y={cy - 12} textAnchor="middle" fill={color} fontSize="20" fontFamily="Arial">
                        {c.centerLine1}
                    </text>
                    <text x={cx} y={cy + 16} textAnchor="middle" fill={color} fontSize="20" fontFamily="Arial">
                        {c.centerLine2}
                    </text>
                    {/* Bottom curved text */}
                    <text fill={color} fontSize="24" fontFamily="Arial" fontWeight="bold">
                        <textPath href={`#${botId}`} startOffset="50%" textAnchor="middle">
                            {c.bottomText} ★ {c.orgName}
                        </textPath>
                    </text>
                </svg>
            </div>
        );
    }

    if (shape === 'rectangle') {
        const w = 280, h = 150;
        const rectSize = size * (h / w);
        return (
            <div style={{ ...containerStyle, width: size, height: rectSize }}>
                <svg viewBox={`0 0 ${w} ${h}`} width={size} height={rectSize}>
                    <rect x="4" y="4" width={w - 8} height={h - 8} fill="none" stroke={color} strokeWidth="6" />
                    <rect x="14" y="14" width={w - 28} height={h - 28} fill="none" stroke={color} strokeWidth="2" />
                    <text x={w / 2} y="52" textAnchor="middle" fill={color} fontSize="24" fontFamily="Arial" fontWeight="bold">{c.orgName}</text>
                    <text x={w / 2} y="76" textAnchor="middle" fill={color} fontSize="13" fontFamily="Arial">{c.topText}</text>
                    <text x={w / 2} y="96" textAnchor="middle" fill={color} fontSize="12" fontFamily="Arial">{c.centerLine1}</text>
                    <text x={w / 2} y="113" textAnchor="middle" fill={color} fontSize="12" fontFamily="Arial">{c.centerLine2}</text>
                    <text x={w / 2} y="130" textAnchor="middle" fill={color} fontSize="12" fontFamily="Arial">{c.bottomText}</text>
                </svg>
            </div>
        );
    }

    // square
    const sq = 240;
    return (
        <div style={{ ...containerStyle, width: size, height: size }}>
            <svg viewBox={`0 0 ${sq} ${sq}`} width={size} height={size}>
                <rect x="4" y="4" width={sq - 8} height={sq - 8} fill="none" stroke={color} strokeWidth="6" />
                <rect x="14" y="14" width={sq - 28} height={sq - 28} fill="none" stroke={color} strokeWidth="2" />
                <text x={sq / 2} y="85" textAnchor="middle" fill={color} fontSize="24" fontFamily="Arial" fontWeight="bold">{c.orgName}</text>
                <text x={sq / 2} y="112" textAnchor="middle" fill={color} fontSize="13" fontFamily="Arial">{c.topText}</text>
                <text x={sq / 2} y="135" textAnchor="middle" fill={color} fontSize="12" fontFamily="Arial">{c.centerLine1}</text>
                <text x={sq / 2} y="155" textAnchor="middle" fill={color} fontSize="12" fontFamily="Arial">{c.centerLine2}</text>
                <text x={sq / 2} y="175" textAnchor="middle" fill={color} fontSize="12" fontFamily="Arial">{c.bottomText}</text>
            </svg>
        </div>
    );
};

export default StampComponent;
